// Utility for handling carton-based M2 rounding and constraints
import { Inventory } from '../services/inventoryService';

export interface CartonConstraint {
  sku: string;
  cartonSize?: number; // M2 per carton
  unitsPerCarton?: number | null;
  cartonDimensions?: string;
  hasConstraints: boolean;
}

export interface M2RoundingResult {
  originalM2: number;
  roundedM2: number;
  cartonCount: number;
  isRounded: boolean;
  message?: string;
}

/**
 * Parse carton dimensions string to calculate M2 per carton
 */
const parseCartonSizeFromDimensions = (dimensions: string): number | undefined => {
  if (!dimensions) {
    return undefined;
  }

  try {
    // Clean the string and extract numbers
    const cleanStr = dimensions.toLowerCase().replace(/\s/g, '');
    
    // Extract numbers using regex - look for patterns like "40x30x25" or "1200x600x20mm"
    const pattern = /(\d+\.?\d*)(mm|cm|m)?/g;
    const matches = [];
    let match;
    while ((match = pattern.exec(cleanStr)) !== null) {
      matches.push(match);
    }

    if (matches.length < 2) {
      return undefined;
    }

    // Extract first two dimensions (length and width)
    const length = parseFloat(matches[0][1]);
    const width = parseFloat(matches[1][1]);
    const lengthUnit = matches[0][2] || '';
    const widthUnit = matches[1][2] || '';

    if (isNaN(length) || isNaN(width)) {
      return undefined;
    }

    // Convert to meters based on units
    let lengthM = length;
    let widthM = width;

    // Handle units for length
    if (lengthUnit === 'mm') {
      lengthM = length / 1000;
    } else if (lengthUnit === 'cm') {
      lengthM = length / 100;
    }
    // If no unit or 'm', assume already in meters

    // Handle units for width  
    if (widthUnit === 'mm') {
      widthM = width / 1000;
    } else if (widthUnit === 'cm') {
      widthM = width / 100;
    }
    // If no unit or 'm', assume already in meters

    // Calculate area in square meters
    const areaM2 = lengthM * widthM;
    
    return Number(areaM2.toFixed(4));
  } catch (error) {
    console.warn('Error parsing carton dimensions:', dimensions, error);
    return undefined;
  }
};

/**
 * Extract carton constraint information from inventory item
 */
export const getCartonConstraints = (inventoryItem: Inventory | null): CartonConstraint => {
  if (!inventoryItem) {
    return {
      sku: '',
      hasConstraints: false
    };
  }

  const unitsPerCarton = inventoryItem.units_per_carton;
  const cartonDimensions = inventoryItem.carton_dimensions;
  
  // Use units_per_carton directly as M2 per carton (no dimension calculation)
  let cartonSize: number | undefined;
  
  if (unitsPerCarton) {
    if (typeof unitsPerCarton === 'string') {
      const numericValue = parseFloat(unitsPerCarton);
      if (!isNaN(numericValue) && numericValue > 0) {
        cartonSize = numericValue;
      }
    } else if (typeof unitsPerCarton === 'number' && unitsPerCarton > 0) {
      cartonSize = unitsPerCarton;
    }
  }

  const hasConstraints = Boolean(cartonSize && cartonSize > 0);

  return {
    sku: inventoryItem.sku_code,
    cartonSize,
    unitsPerCarton,
    cartonDimensions,
    hasConstraints
  };
};

/**
 * Calculate M2 rounding based on carton constraints
 */
export const calculateM2Rounding = (
  requestedM2: number,
  cartonConstraint: CartonConstraint
): M2RoundingResult => {
  if (!cartonConstraint.hasConstraints || !cartonConstraint.cartonSize || cartonConstraint.cartonSize <= 0) {
    return {
      originalM2: requestedM2,
      roundedM2: requestedM2,
      cartonCount: 0,
      isRounded: false
    };
  }

  const { cartonSize } = cartonConstraint;
  
  // Calculate how many cartons are needed
  const exactCartons = requestedM2 / cartonSize;
  const requiredCartons = Math.ceil(exactCartons); // Always round up to ensure we have enough material
  
  // Calculate the rounded M2 based on full cartons
  const roundedM2 = requiredCartons * cartonSize;
  
  const isRounded = Math.abs(roundedM2 - requestedM2) > 0.001; // Allow for small floating point differences
  
  let message: string | undefined;
  if (isRounded) {
    message = `Will be rounded to ${roundedM2.toFixed(2)} M² (${requiredCartons} carton${requiredCartons > 1 ? 's' : ''} × ${cartonSize} M²/carton)`;
  }

  return {
    originalM2: requestedM2,
    roundedM2: Number(roundedM2.toFixed(4)), // Round to 4 decimal places to avoid floating point issues
    cartonCount: requiredCartons,
    isRounded,
    message
  };
};

/**
 * Validate if an M2 quantity aligns with carton constraints
 */
export const validateM2Quantity = (
  m2Quantity: number,
  cartonConstraint: CartonConstraint
): { isValid: boolean; suggestedM2?: number; message?: string } => {
  if (!cartonConstraint.hasConstraints || !cartonConstraint.cartonSize) {
    return { isValid: true };
  }

  const { cartonSize } = cartonConstraint;
  const exactCartons = m2Quantity / cartonSize;
  const isWholeCartons = Math.abs(exactCartons - Math.round(exactCartons)) < 0.001;

  if (isWholeCartons) {
    return { isValid: true };
  }

  const suggestedCartons = Math.ceil(exactCartons);
  const suggestedM2 = suggestedCartons * cartonSize;

  return {
    isValid: false,
    suggestedM2: Number(suggestedM2.toFixed(4)),
    message: `Suggested: ${suggestedM2.toFixed(2)} M² (${suggestedCartons} carton${suggestedCartons > 1 ? 's' : ''})`
  };
};

/**
 * Format carton constraint information for display
 */
export const formatCartonInfo = (cartonConstraint: CartonConstraint): string => {
  if (!cartonConstraint.hasConstraints) {
    return '';
  }

  const parts: string[] = [];
  
  if (cartonConstraint.cartonSize) {
    parts.push(`${cartonConstraint.cartonSize} M²/carton`);
  }
  
  if (cartonConstraint.unitsPerCarton) {
    parts.push(`${cartonConstraint.unitsPerCarton} units/carton`);
  }

  if (cartonConstraint.cartonDimensions) {
    parts.push(`Dimensions: ${cartonConstraint.cartonDimensions}`);
  }

  return parts.join(' • ');
}; 